import axiosInstance from './axios';

export interface GetSessionsParams {
  page?: number;
  limit?: number;
  sortField?: string;
  sortValue?: string;
  siteId?: string | number;
  [key: string]: any;
}

export type GameStatsExportPeriod = 'previous_month' | 'all_time';
export type GameStatsExportStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface GameStatsExportItem {
  id: number;
  siteId: number;
  period: GameStatsExportPeriod;
  periodFrom: string | null;
  periodTo: string | null;
  status: GameStatsExportStatus;
  fileName: string | null;
  sizeBytes: number | null;
  error: string | null;
  startedAt: string | null;
  completedAt: string | null;
  expiresAt: string;
  createdAt: string;
}

export const gamesService = {
  getActiveSessions: async (params: GetSessionsParams): Promise<any> => {
    const response = await axiosInstance.get('/admin/games/sessions', { params });
    return response.data;
  },
  watchSession: async (id: number, siteId: string | number): Promise<any> => {
    const response = await axiosInstance.post(
      `/admin/games/sessions/${id}/watch`,
      {},
      { params: { siteId } },
    );
    return response.data;
  },
  unwatchSession: async (id: number, siteId: string | number): Promise<any> => {
    const response = await axiosInstance.post(
      `/admin/games/sessions/${id}/unwatch`,
      {},
      { params: { siteId } },
    );
    return response.data;
  },
  getGameStats: async (params: GetSessionsParams): Promise<any> => {
    const response = await axiosInstance.get('/admin/games/stats', { params });
    return response.data;
  },
  getFreeSpinsGames: async (params: GetSessionsParams): Promise<any> => {
    const response = await axiosInstance.get('/admin/games/free-spins', { params });
    return response.data;
  },
  getGameBetLevels: async (
    gameId: string | number,
    siteId: string | number,
    currencies: string,
  ): Promise<any> => {
    const response = await axiosInstance.get(`/admin/games/${gameId}/bet-levels`, {
      params: { siteId, currencies },
    });
    return response.data;
  },
  getProviderList: async (): Promise<any> => {
    const response = await axiosInstance.get('/admin/games/provider-list');
    return response.data;
  },
  getSubProviderList: async (params: any): Promise<any> => {
    const response = await axiosInstance.get('/admin/games/sub-provider-list', { params });
    return response.data;
  },
  toggleSubProvider: async (id: string | number, isEnabled: boolean): Promise<any> => {
    const response = await axiosInstance.patch(`/admin/games/sub-providers/${id}/toggle`, {
      isEnabled,
    });
    return response.data;
  },
  updateSubProvider: async (id: string | number, data: any): Promise<any> => {
    const response = await axiosInstance.patch(`/admin/games/sub-providers/${id}`, data);
    return response.data;
  },
  toggleGame: async (id: string | number, isEnabled: boolean): Promise<any> => {
    const response = await axiosInstance.patch(`/admin/games/${id}/toggle`, { isEnabled });
    return response.data;
  },
  getGamesBySubProvider: async (params: {
    subProviderId: string | number;
    page?: number;
    pageSize?: number;
    [key: string]: any;
  }): Promise<any> => {
    const response = await axiosInstance.get('/admin/games/by-sub-provider', { params });
    return response.data;
  },
  syncGames: async (data: {
    siteId: number;
    providerCode?: string;
    full: boolean;
    providers?: string[];
    featureGroups?: string[];
  }): Promise<any> => {
    const response = await axiosInstance.post('/admin/games/catalog/sync', data);
    return response.data;
  },
  getSyncStatus: async (runId: number): Promise<any> => {
    const response = await axiosInstance.get(`/admin/games/catalog/sync/${runId}`);
    return response.data;
  },
  createGameStatsExport: async (data: {
    siteId: number;
    period: GameStatsExportPeriod;
  }): Promise<any> => {
    const response = await axiosInstance.post('/admin/games/stats/exports', data);
    return response.data;
  },
  getGameStatsExport: async (exportId: number): Promise<any> => {
    const response = await axiosInstance.get(`/admin/games/stats/exports/${exportId}`);
    return response.data;
  },
  listGameStatsExports: async (siteId: number): Promise<any> => {
    const response = await axiosInstance.get('/admin/games/stats/exports', {
      params: { siteId },
    });
    return response.data;
  },
  downloadGameStatsExport: async (exportId: number): Promise<Blob> => {
    const response = await axiosInstance.get(`/admin/games/stats/exports/${exportId}/download`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
};
